// When a new build may replace the page the user is looking at, without
// them pressing Update. The service worker registers with registerType
// "prompt" (vite.config.ts): a new build's worker WAITS until something
// applies it (skipWaiting + reload), because autoUpdate once reloaded the
// phone app mid-typing. PwaUpdatePrompt asks this module.
//
// A waiting build is applied without asking:
//   - while the app is in the background (it reloads while you are away);
//   - on a launch or a return to the foreground, before you have touched
//     anything: a build already waiting is applied at once, and one found
//     in the first minute (the browser checks on a launch, PwaUpdatePrompt
//     on every return) as soon as it has installed;
// and NEVER while a reload would lose something, in this tab or any other
// open tab of the dashboard: a message typed or a file picked and not sent,
// a reply streaming in, a meeting recording or its upload, text in the
// field you are in or in any text area (updateHold.ts). Otherwise the toast
// offers Update, as before, and the next trip to the background or back
// applies it. A tab that holds when a build takes over anyway (Update
// pressed in another tab) keeps its page and reloads by these same rules.
//
// Why (2026-09-28: the operator's phone printed MEDIA paths in the chat
// exactly as the build before 04:28 does; the 04:28 build shows players, so
// the phone was still on the previous build). An installed PWA on iOS is
// mostly RESUMED, not launched, and a resume is not a navigation, so the
// browser does not look for a new build; before this only an hourly timer
// did, and timers do not run while the app is suspended. Measured on the
// previous code in Chromium (a scripted launch and resume against two
// builds): a return two minutes after a deploy found nothing (old build, no
// toast); a launch after a deploy showed the toast once the new build had
// installed but kept running the old one until Update or the next trip to
// the background.

/** How long after a launch or a return an untouched page may still reload into a new build. */
export const FRESH_MS = 60_000;
/** At most one update check per this long on returns to the foreground. */
export const CHECK_EVERY_MS = 60_000;

export interface UpdateMoment {
  /** The page is in the background. */
  hidden: boolean;
  /** When it was last launched or brought back to the foreground (ms). */
  shownAt: number;
  /** A tap, click, key or scroll wheel since then. */
  interacted: boolean;
  /** Something a reload would lose, here or (when applying a build) in another tab (updateHold.ts). */
  held: boolean;
  now: number;
}

/** Whether a waiting build may be applied now, unasked. */
export function mayApplyUpdate({ hidden, shownAt, interacted, held, now }: UpdateMoment): boolean {
  if (held) return false;
  if (hidden) return true;
  return !interacted && now - shownAt <= FRESH_MS;
}

/** Whether a return to the foreground should ask the server for a new build. */
export function shouldCheckForUpdate(lastCheckAt: number, now: number): boolean {
  return now - lastCheckAt >= CHECK_EVERY_MS;
}
